import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, Loader2, ArrowLeft, FileQuestion, Package, Truck, Phone, Mail, Info } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InquiryCreate = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('header');
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  
  // Master Data
  const [customers, setCustomers] = useState([]);
  const [salesOrgs, setSalesOrgs] = useState([]);
  const [distChannels, setDistChannels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [orderReasons, setOrderReasons] = useState([]);
  
  // Customer-specific data
  const [customerSalesAreas, setCustomerSalesAreas] = useState([]);
  const [salesAreaError, setSalesAreaError] = useState('');
  const [shippingPointsForPlant, setShippingPointsForPlant] = useState({});

  const [header, setHeader] = useState({
    document_type: 'IN',
    customer_id: '',
    sales_organization: '',
    distribution_channel: '',
    division: '',
    inquiry_date: new Date().toISOString().split('T')[0],
    pricing_date: new Date().toISOString().split('T')[0],
    requested_delivery_date: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    sold_to_party: '',
    ship_to_party: '',
    mode_of_communication: '',
    incoterms: '',
    incoterms_location: '',
    payment_terms: '',
    order_reason: '',
    customer_reference: '',
    header_note: ''
  });

  const [items, setItems] = useState([{
    item_number: 10,
    material_id: '',
    item_description: '',
    requested_quantity: 1,
    unit_of_measure: 'EA',
    plant: '',
    plant_determined_from: '',
    shipping_point: '',
    target_price: '',
    pricing_info: '',
    // Indicative pricing from condition records
    indicative_pricing: null,
    pricing_loading: false,
    availability_check: false,
    customer_material_number: '',
    item_note: ''
  }]);

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    setIsLoading(true);
    try {
      const [custRes, orgRes, dcRes, divRes, matRes, plantRes, incoRes, payRes, orderReasonRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/sales-organizations`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/distribution-channels`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/sales-distribution/divisions`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/materials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/material-management/plants`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-incoterms`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/customer-terms-of-payments`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/presales/order-reasons`).catch(() => ({ data: [] }))
      ]);
      setCustomers(custRes.data || []);
      setSalesOrgs(orgRes.data || []);
      setDistChannels(dcRes.data || []);
      setDivisions(divRes.data || []);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setIncoterms(incoRes.data || []);
      setPaymentTerms(payRes.data || []);
      setOrderReasons(orderReasonRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch commercial terms from Ship-to Party's customer master
  const fetchCommercialTerms = async (shipToPartyId, salesOrg, distChannel) => {
    if (!shipToPartyId || !salesOrg || !distChannel) return;
    
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/presales/customer-commercial-terms/${shipToPartyId}`,
        { params: { sales_org: salesOrg, dist_channel: distChannel } }
      );
      
      if (response.data) {
        setHeader(prev => ({
          ...prev,
          incoterms: response.data.incoterms || '',
          incoterms_location: response.data.incoterms_location || '',
          payment_terms: response.data.payment_terms || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching commercial terms:', error);
    }
  };

  // Fetch shipping points for a plant
  const fetchShippingPointsForPlant = async (plantId) => {
    if (!plantId || shippingPointsForPlant[plantId]) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/shipping-points-for-plant/${plantId}`);
      setShippingPointsForPlant(prev => ({
        ...prev,
        [plantId]: response.data || []
      }));
    } catch (error) {
      console.error('Error fetching shipping points:', error);
    }
  };

  // Determine plant for item
  const determinePlantForItem = async (index, materialId) => {
    const shipToParty = header.ship_to_party || header.customer_id;
    if (!shipToParty || !header.sales_organization || !header.distribution_channel) return;
    
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/determine-plant`, {
        params: {
          material_id: materialId || undefined,
          ship_to_party: shipToParty,
          sales_org: header.sales_organization,
          dist_channel: header.distribution_channel
        }
      });
      
      if (response.data?.plant_id) {
        const newItems = [...items];
        newItems[index].plant = response.data.plant_id;
        newItems[index].plant_determined_from = response.data.source;
        setItems(newItems);
        
        // Fetch shipping points for this plant
        fetchShippingPointsForPlant(response.data.plant_id);
      }
    } catch (error) {
      console.error('Error determining plant:', error);
    }
  };

  const handleCustomerChange = (customerId) => {
    setSalesAreaError('');
    setCustomerSalesAreas([]);
    
    if (!customerId) {
      setHeader({
        ...header,
        customer_id: '',
        sold_to_party: '',
        ship_to_party: '',
        sales_organization: '',
        distribution_channel: '',
        incoterms: '',
        incoterms_location: '',
        payment_terms: ''
      });
      return;
    }
    
    const selectedCustomer = customers.find(c => c.customer_id === customerId);
    
    if (selectedCustomer) {
      const salesAreas = selectedCustomer.sales_area_data || [];
      
      if (salesAreas.length === 0) {
        setSalesAreaError(`Error: Customer ${customerId} is not assigned to any Sales Area. Please maintain Sales Area data in Customer Master.`);
        setCustomerSalesAreas([]);
      } else {
        setCustomerSalesAreas(salesAreas);
        setSalesAreaError('');
      }
    }
    
    setHeader({
      ...header,
      customer_id: customerId,
      sold_to_party: customerId,
      ship_to_party: customerId,
      sales_organization: '',
      distribution_channel: '',
      division: '',
      incoterms: '',
      incoterms_location: '',
      payment_terms: ''
    });
  };

  // Get unique sales orgs from customer's sales areas
  const getCustomerSalesOrgs = () => {
    if (customerSalesAreas.length === 0) return [];
    const uniqueOrgs = [...new Set(customerSalesAreas.map(sa => sa.sales_org_id))];
    return salesOrgs.filter(org => uniqueOrgs.includes(org.sales_org_id));
  };

  // Get distribution channels for selected sales org from customer's sales areas
  const getCustomerDistChannels = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization) return [];
    const relevantAreas = customerSalesAreas.filter(sa => sa.sales_org_id === header.sales_organization);
    const uniqueChannels = [...new Set(relevantAreas.map(sa => sa.dist_channel_id))];
    return distChannels.filter(dc => uniqueChannels.includes(dc.dist_channel_id));
  };

  // Get divisions for selected sales org and distribution channel from customer's sales areas
  const getCustomerDivisions = () => {
    if (customerSalesAreas.length === 0 || !header.sales_organization || !header.distribution_channel) return [];
    const relevantAreas = customerSalesAreas.filter(
      sa => sa.sales_org_id === header.sales_organization && sa.dist_channel_id === header.distribution_channel
    );
    const uniqueDivisions = [...new Set(relevantAreas.map(sa => sa.division_id))];
    return divisions.filter(d => uniqueDivisions.includes(d.division_id));
  };

  // Handle sales org change - reset distribution channel and division
  const handleSalesOrgChange = (salesOrgId) => {
    setHeader({
      ...header,
      sales_organization: salesOrgId,
      distribution_channel: '',
      division: '',
      incoterms: '',
      incoterms_location: '',
      payment_terms: ''
    });
  };

  // Handle distribution channel change - reset division and fetch commercial terms from Ship-to Party
  const handleDistChannelChange = (distChannelId) => {
    const newHeader = {
      ...header,
      distribution_channel: distChannelId,
      division: ''
    };
    setHeader(newHeader);
    
    // Fetch commercial terms from Ship-to Party's customer master
    const shipToParty = header.ship_to_party || header.customer_id;
    if (shipToParty && header.sales_organization && distChannelId) {
      fetchCommercialTerms(shipToParty, header.sales_organization, distChannelId);
    }
  };

  // Get ship-to party options (customers that can be ship-to)
  const getShipToPartyOptions = () => {
    // For simplicity, return all customers. In SAP, this would be filtered by partner functions
    return customers;
  };

  // Handle ship-to party change - fetch commercial terms and re-determine plants for all items
  const handleShipToPartyChange = (shipToPartyId) => {
    setHeader({
      ...header,
      ship_to_party: shipToPartyId
    });
    
    // Fetch commercial terms from Ship-to Party's customer master
    if (shipToPartyId && header.sales_organization && header.distribution_channel) {
      fetchCommercialTerms(shipToPartyId, header.sales_organization, header.distribution_channel);
    }
    
    // Re-determine plants for all items
    items.forEach((item, index) => {
      if (item.material_id) {
        determinePlantForItem(index, item.material_id);
      }
    });
  };

  // Get materials available for the selected Sales Org and Distribution Channel
  const getAvailableMaterials = () => {
    if (!header.sales_organization || !header.distribution_channel) {
      return [];
    }
    
    return materials.filter(m => {
      const salesData = m.sales_data || [];
      return salesData.some(
        sd => sd.sales_org_id === header.sales_organization && 
              sd.dist_channel_id === header.distribution_channel
      );
    });
  };

  // Check if a material is available in the selected sales area
  const isMaterialAvailable = (materialNumber) => {
    if (!materialNumber || !header.sales_organization || !header.distribution_channel) {
      return false;
    }
    
    const material = materials.find(m => m.material_number === materialNumber);
    if (!material) return false;
    
    const salesData = material.sales_data || [];
    return salesData.some(
      sd => sd.sales_org_id === header.sales_organization && 
            sd.dist_channel_id === header.distribution_channel
    );
  };

  // Get error message for unavailable material
  const getMaterialError = (materialNumber) => {
    if (!materialNumber) return null;
    if (!header.sales_organization || !header.distribution_channel) {
      return 'Please select Sales Organization and Distribution Channel first';
    }
    
    const material = materials.find(m => m.material_number === materialNumber);
    if (!material) return `Material ${materialNumber} not found`;
    
    if (!isMaterialAvailable(materialNumber)) {
      return `Material ${materialNumber} is not defined for Sales Org: ${header.sales_organization}, Dist.Chan: ${header.distribution_channel}`;
    }
    
    return null;
  };

  const handleAddItem = () => {
    const newItemNumber = items.length > 0 ? Math.max(...items.map(i => i.item_number)) + 10 : 10;
    setItems([...items, {
      item_number: newItemNumber,
      material_id: '',
      item_description: '',
      requested_quantity: 1,
      unit_of_measure: 'EA',
      plant: '',
      plant_determined_from: '',
      shipping_point: '',
      target_price: '',
      pricing_info: '',
      indicative_pricing: null,
      pricing_loading: false,
      availability_check: false,
      customer_material_number: '',
      item_note: ''
    }]);
    setActiveItemIndex(items.length);
  };

  const handleRemoveItem = (index) => {
    if (items.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    if (activeItemIndex >= newItems.length) {
      setActiveItemIndex(newItems.length - 1);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    
    // Auto-fill description and determine plant when material is selected
    if (field === 'material_id' && value) {
      const material = materials.find(m => m.material_number === value);
      if (material) {
        newItems[index].item_description = material.description || '';
        newItems[index].unit_of_measure = material.base_uom || 'EA';
      }
      setItems(newItems);
      // Determine plant for this item
      determinePlantForItem(index, value);
      // Fetch indicative pricing
      fetchIndicativePricing(index, value, newItems[index].requested_quantity);
    } else if (field === 'requested_quantity' && value) {
      setItems(newItems);
      // Re-fetch pricing when quantity changes
      if (newItems[index].material_id) {
        fetchIndicativePricing(index, newItems[index].material_id, value);
      }
    } else if (field === 'plant' && value) {
      newItems[index].plant_determined_from = 'MANUAL';
      newItems[index].shipping_point = ''; // Reset shipping point when plant changes
      setItems(newItems);
      // Fetch shipping points for this plant
      fetchShippingPointsForPlant(value);
    } else {
      setItems(newItems);
    }
  };

  // Fetch indicative pricing from condition records
  const fetchIndicativePricing = async (itemIndex, materialId, quantity) => {
    if (!materialId || !header.customer_id) return;
    
    // Set loading state
    setItems(prev => {
      const updated = [...prev];
      updated[itemIndex] = { ...updated[itemIndex], pricing_loading: true };
      return updated;
    });
    
    try {
      const params = new URLSearchParams({
        material_id: materialId,
        quantity: quantity || 1
      });
      
      if (header.customer_id) params.append('customer_id', header.customer_id);
      if (header.sales_organization) params.append('sales_org', header.sales_organization);
      if (header.distribution_channel) params.append('distribution_channel', header.distribution_channel);
      
      const response = await axios.get(`${BACKEND_URL}/api/pricing/indicative-price?${params.toString()}`);
      
      setItems(prev => {
        const updated = [...prev];
        updated[itemIndex] = {
          ...updated[itemIndex],
          indicative_pricing: response.data,
          pricing_loading: false,
          // Auto-fill target price with net price if not already set
          target_price: updated[itemIndex].target_price || (response.data.net_price ? response.data.net_price.toString() : '')
        };
        return updated;
      });
    } catch (error) {
      console.error('Error fetching indicative pricing:', error);
      setItems(prev => {
        const updated = [...prev];
        updated[itemIndex] = {
          ...updated[itemIndex],
          indicative_pricing: null,
          pricing_loading: false
        };
        return updated;
      });
    }
  };

  const validateForm = () => {
    if (!header.customer_id) {
      toast.error('Customer is required');
      return false;
    }
    
    if (customerSalesAreas.length === 0) {
      toast.error('Error: The Customer is not present in any Sales Area. Please maintain Sales Area data in Customer Master.');
      return false;
    }
    
    if (!header.sales_organization) {
      toast.error('Sales Organization is required');
      return false;
    }
    if (!header.distribution_channel) {
      toast.error('Distribution Channel is required');
      return false;
    }
    if (!header.division) {
      toast.error('Division is required');
      return false;
    }
    
    const validSalesArea = customerSalesAreas.some(
      sa => sa.sales_org_id === header.sales_organization && 
            sa.dist_channel_id === header.distribution_channel &&
            sa.division_id === header.division
    );
    if (!validSalesArea) {
      toast.error('Error: The selected Sales Area (Sales Org + Distribution Channel + Division) is not valid for this Customer');
      return false;
    }
    
    if (!header.inquiry_date) {
      toast.error('Inquiry Date is required');
      return false;
    }
    
    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    // 1. Inquiry date should not be in the future (can be today or past for backdating)
    if (header.inquiry_date) {
      const inquiryDate = new Date(header.inquiry_date);
      inquiryDate.setHours(0, 0, 0, 0);
      if (inquiryDate > today) {
        toast.error('Inquiry Date cannot be in the future');
        return false;
      }
    }
    
    // 2. Valid From date should not be in the past
    if (header.valid_from) {
      const validFromDate = new Date(header.valid_from);
      validFromDate.setHours(0, 0, 0, 0);
      if (validFromDate < today) {
        toast.error('Valid From date cannot be in the past');
        return false;
      }
    }
    
    // 3. Valid To date should not be in the past
    if (header.valid_to) {
      const validToDate = new Date(header.valid_to);
      validToDate.setHours(0, 0, 0, 0);
      if (validToDate < today) {
        toast.error('Valid To date cannot be in the past');
        return false;
      }
    }
    
    // 4. Valid To must be greater than Valid From
    if (header.valid_from && header.valid_to) {
      const validFromDate = new Date(header.valid_from);
      const validToDate = new Date(header.valid_to);
      if (validToDate <= validFromDate) {
        toast.error('Valid To date must be greater than Valid From date');
        return false;
      }
    }
    
    for (const item of items) {
      if (!item.item_description) {
        toast.error(`Item ${item.item_number}: Description is required`);
        return false;
      }
      if (!item.requested_quantity || item.requested_quantity <= 0) {
        toast.error(`Item ${item.item_number}: Quantity must be greater than 0`);
        return false;
      }
      if (item.material_id) {
        const materialError = getMaterialError(item.material_id);
        if (materialError) {
          toast.error(`Item ${item.item_number}: ${materialError}`);
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSaving(true);
    try {
      const payload = {
        ...header,
        items: items.map(item => ({
          item_number: parseInt(item.item_number),
          material_id: item.material_id || null,
          item_description: item.item_description,
          requested_quantity: parseInt(item.requested_quantity) || 0,
          unit_of_measure: item.unit_of_measure || 'EA',
          plant: item.plant || null,
          shipping_point: item.shipping_point || null,
          target_price: item.target_price ? parseFloat(item.target_price) : null,
          pricing_info: item.pricing_info || null,
          availability_check: item.availability_check || null,
          customer_material_number: item.customer_material_number || null,
          item_note: item.item_note || null
        }))
      };
      
      console.log('Sending inquiry payload:', JSON.stringify(payload, null, 2));
      
      const response = await axios.post(`${BACKEND_URL}/api/presales/inquiries`, payload);
      toast.success(`Inquiry '${response.data.inquiry_number}' created successfully!`);
      navigate(`/presales/inquiries/${response.data.inquiry_number}`);
    } catch (error) {
      console.error('Error creating inquiry:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          const errorMessages = error.response.data.detail.map(err => 
            `${err.loc.join('.')}: ${err.msg}`
          ).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error('Failed to create inquiry. Please check all required fields.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const currentItem = items[activeItemIndex] || items[0];
  const currentItemShippingPoints = currentItem.plant ? (shippingPointsForPlant[currentItem.plant] || []) : [];

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="inquiry-create">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/presales/inquiries')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileQuestion className="w-6 h-6" />
                Create Inquiry
              </h1>
              <p className="text-sm text-gray-600">Record a new customer inquiry</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-inquiry-btn">
            {isSaving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Create Inquiry</>
            )}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="header">Header Data</TabsTrigger>
            <TabsTrigger value="partners">Partners & Terms</TabsTrigger>
            <TabsTrigger value="items">Items ({items.length})</TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <TabsContent value="header">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Customer & Organization */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Customer & Organization</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Document Type</Label>
                    <Input value="IN - Inquiry" disabled className="bg-gray-50" />
                  </div>
                  <div>
                    <Label>Customer *</Label>
                    <Select value={header.customer_id || '__SELECT__'} onValueChange={(v) => handleCustomerChange(v === '__SELECT__' ? '' : v)}>
                      <SelectTrigger data-testid="customer-select"><SelectValue placeholder="Select Customer" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__SELECT__">Select Customer</SelectItem>
                        {customers.map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {salesAreaError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {salesAreaError}
                    </div>
                  )}
                  
                  {header.customer_id && customerSalesAreas.length > 0 && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                      Customer has {customerSalesAreas.length} Sales Area(s) configured
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Sales Organization *</Label>
                      <Select 
                        value={header.sales_organization || '__SELECT__'} 
                        onValueChange={(v) => handleSalesOrgChange(v === '__SELECT__' ? '' : v)}
                        disabled={!header.customer_id || customerSalesAreas.length === 0}
                      >
                        <SelectTrigger data-testid="sales-org-select">
                          <SelectValue placeholder={!header.customer_id ? "Select Customer first" : "Select Sales Org"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Sales Org</SelectItem>
                          {getCustomerSalesOrgs().map(org => (
                            <SelectItem key={org.sales_org_id} value={org.sales_org_id}>
                              {org.sales_org_id} - {org.sales_org_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Distribution Channel *</Label>
                      <Select 
                        value={header.distribution_channel || '__SELECT__'} 
                        onValueChange={(v) => handleDistChannelChange(v === '__SELECT__' ? '' : v)}
                        disabled={!header.sales_organization}
                      >
                        <SelectTrigger data-testid="dist-channel-select">
                          <SelectValue placeholder={!header.sales_organization ? "Select Sales Org first" : "Select Channel"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__SELECT__">Select Channel</SelectItem>
                          {getCustomerDistChannels().map(dc => (
                            <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                              {dc.dist_channel_id} - {dc.dist_channel_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Division *</Label>
                    <Select 
                      value={header.division || '__SELECT__'} 
                      onValueChange={(v) => setHeader({...header, division: v === '__SELECT__' ? '' : v})}
                      disabled={!header.distribution_channel}
                    >
                      <SelectTrigger data-testid="division-select">
                        <SelectValue placeholder={!header.distribution_channel ? "Select Distribution Channel first" : "Select Division"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__SELECT__">Select Division</SelectItem>
                        {getCustomerDivisions().map(d => (
                          <SelectItem key={d.division_id} value={d.division_id}>
                            {d.division_id} - {d.division_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {header.division && (
                      <p className="text-xs text-green-600 mt-1">
                        Sales Area: {header.sales_organization} / {header.distribution_channel} / {header.division}
                      </p>
                    )}
                  </div>
                </div>
              </Card>

              {/* Dates & References */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Dates & References</h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Inquiry Date *</Label>
                      <Input
                        type="date"
                        value={header.inquiry_date}
                        onChange={(e) => setHeader({...header, inquiry_date: e.target.value})}
                        max={new Date().toISOString().split('T')[0]}
                        data-testid="inquiry-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">Document date (cannot be future)</p>
                    </div>
                    <div>
                      <Label>Pricing Date</Label>
                      <Input
                        type="date"
                        value={header.pricing_date}
                        onChange={(e) => setHeader({...header, pricing_date: e.target.value})}
                        data-testid="pricing-date"
                      />
                      <p className="text-xs text-gray-500 mt-1">Defaults to today</p>
                    </div>
                  </div>
                  <div>
                    <Label>Requested Delivery Date</Label>
                    <Input
                      type="date"
                      value={header.requested_delivery_date}
                      onChange={(e) => setHeader({...header, requested_delivery_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      data-testid="requested-delivery-date"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Valid From</Label>
                      <Input
                        type="date"
                        value={header.valid_from}
                        onChange={(e) => setHeader({...header, valid_from: e.target.value})}
                        min={new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Cannot be in the past</p>
                      {header.valid_from && new Date(header.valid_from) < new Date(new Date().toISOString().split('T')[0]) && (
                        <p className="text-xs text-red-600 mt-1">Date is in the past</p>
                      )}
                    </div>
                    <div>
                      <Label>Valid To</Label>
                      <Input
                        type="date"
                        value={header.valid_to}
                        onChange={(e) => setHeader({...header, valid_to: e.target.value})}
                        min={header.valid_from || new Date().toISOString().split('T')[0]}
                      />
                      <p className="text-xs text-gray-500 mt-1">Must be greater than Valid From</p>
                      {header.valid_from && header.valid_to && new Date(header.valid_to) <= new Date(header.valid_from) && (
                        <p className="text-xs text-red-600 mt-1">Must be after Valid From date</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Order Reason</Label>
                    <Select 
                      value={header.order_reason || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, order_reason: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="order-reason-select">
                        <SelectValue placeholder="Select Order Reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {orderReasons.map(r => (
                          <SelectItem key={r.code} value={r.code}>
                            {r.code} - {r.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Customer Reference</Label>
                    <Input
                      value={header.customer_reference}
                      onChange={(e) => setHeader({...header, customer_reference: e.target.value})}
                      placeholder="Customer's reference number"
                      maxLength={35}
                    />
                  </div>
                  <div>
                    <Label>Header Note</Label>
                    <textarea
                      value={header.header_note}
                      onChange={(e) => setHeader({...header, header_note: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={3}
                      placeholder="Additional notes..."
                      maxLength={1000}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Partners & Terms Tab */}
          <TabsContent value="partners">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Partner Functions */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Truck className="w-5 h-5" />
                  Partner Functions
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label>Sold-to Party</Label>
                    <Input 
                      value={header.sold_to_party ? `${header.sold_to_party} - ${customers.find(c => c.customer_id === header.sold_to_party)?.name || ''}` : ''} 
                      disabled 
                      className="bg-gray-50" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Same as Customer</p>
                  </div>
                  <div>
                    <Label>Ship-to Party</Label>
                    <Select 
                      value={header.ship_to_party || '__DEFAULT__'} 
                      onValueChange={(v) => handleShipToPartyChange(v === '__DEFAULT__' ? header.customer_id : v)}
                      disabled={!header.customer_id}
                    >
                      <SelectTrigger data-testid="ship-to-party-select">
                        <SelectValue placeholder="Select Ship-to Party" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__DEFAULT__">Same as Sold-to Party</SelectItem>
                        {getShipToPartyOptions().map(c => (
                          <SelectItem key={c.customer_id} value={c.customer_id}>
                            {c.customer_id} - {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Plant is determined from Ship-to Party's delivering plant
                    </p>
                  </div>
                  <div>
                    <Label>Mode of Communication</Label>
                    <Select 
                      value={header.mode_of_communication || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, mode_of_communication: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="communication-mode-select">
                        <SelectValue placeholder="Select Mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        <SelectItem value="PHONE">
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" /> Phone
                          </div>
                        </SelectItem>
                        <SelectItem value="EMAIL">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4" /> Email
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              {/* Commercial Terms */}
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Commercial Terms</h3>
                <div className="space-y-4">
                  {header.sales_organization && header.distribution_channel && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>These terms are automatically fetched from <strong>Ship-to Party's</strong> Customer Master for the selected Sales Area. You can override them if needed.</span>
                    </div>
                  )}
                  <div>
                    <Label>Incoterms</Label>
                    <Select 
                      value={header.incoterms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, incoterms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="incoterms-select">
                        <SelectValue placeholder="Select Incoterms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {incoterms.map(i => (
                          <SelectItem key={i.incoterms_id} value={i.incoterms_id}>
                            {i.incoterms_id} - {i.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Incoterms Location</Label>
                    <Input
                      value={header.incoterms_location}
                      onChange={(e) => setHeader({...header, incoterms_location: e.target.value})}
                      placeholder="e.g., Port of destination"
                      maxLength={70}
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select 
                      value={header.payment_terms || '__NONE__'} 
                      onValueChange={(v) => setHeader({...header, payment_terms: v === '__NONE__' ? '' : v})}
                    >
                      <SelectTrigger data-testid="payment-terms-select">
                        <SelectValue placeholder="Select Payment Terms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- None --</SelectItem>
                        {paymentTerms.map(p => (
                          <SelectItem key={p.payment_key} value={p.payment_key}>
                            {p.payment_key} - {p.sales_text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Item List */}
              <Card className="p-4 lg:col-span-1">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Items</h3>
                  <Button size="sm" onClick={handleAddItem} data-testid="add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${
                        activeItemIndex === index ? 'bg-orange-100 border-2 border-orange-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => setActiveItemIndex(index)}
                      data-testid={`item-${item.item_number}`}
                    >
                      <div>
                        <p className="font-mono font-bold text-sm">{item.item_number.toString().padStart(4, '0')}</p>
                        <p className="text-xs text-gray-600 truncate max-w-[120px]">
                          {item.item_description || item.material_id || 'New Item'}
                        </p>
                      </div>
                      {items.length > 1 && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </Card>

              {/* Item Details */}
              <Card className="p-6 lg:col-span-3">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Item {currentItem.item_number.toString().padStart(4, '0')} Details
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Material Selection */}
                  <div>
                    <Label>Material</Label>
                    <Select 
                      value={currentItem.material_id || '__NONE__'} 
                      onValueChange={(v) => handleItemChange(activeItemIndex, 'material_id', v === '__NONE__' ? '' : v)}
                      disabled={!header.sales_organization || !header.distribution_channel}
                    >
                      <SelectTrigger data-testid="item-material-select">
                        <SelectValue placeholder={
                          !header.sales_organization || !header.distribution_channel 
                            ? "Select Sales Area first" 
                            : "Select Material"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__NONE__">-- Manual Entry --</SelectItem>
                        {getAvailableMaterials().length > 0 ? (
                          getAvailableMaterials().map(m => (
                            <SelectItem key={m.material_number} value={m.material_number}>
                              {m.material_number} - {m.description}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="__NO_MATERIALS__" disabled>
                            No materials for this Sales Area
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {header.sales_organization && header.distribution_channel && (
                      <p className="text-xs text-gray-500 mt-1">
                        {getAvailableMaterials().length} material(s) available
                      </p>
                    )}
                    {currentItem.material_id && getMaterialError(currentItem.material_id) && (
                      <p className="text-xs text-red-600 mt-1 p-2 bg-red-50 rounded">
                        {getMaterialError(currentItem.material_id)}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label>Customer Material #</Label>
                    <Input
                      value={currentItem.customer_material_number}
                      onChange={(e) => handleItemChange(activeItemIndex, 'customer_material_number', e.target.value)}
                      placeholder="Customer's material code"
                      maxLength={35}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Description *</Label>
                    <Input
                      value={currentItem.item_description}
                      onChange={(e) => handleItemChange(activeItemIndex, 'item_description', e.target.value)}
                      placeholder="Item description"
                      maxLength={100}
                      data-testid="item-description"
                    />
                  </div>
                  
                  <div>
                    <Label>Requested Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      value={currentItem.requested_quantity}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow whole numbers
                        if (value === '' || /^\d+$/.test(value)) {
                          handleItemChange(activeItemIndex, 'requested_quantity', value);
                        }
                      }}
                      onKeyDown={(e) => {
                        // Prevent decimal point and 'e' for scientific notation
                        if (e.key === '.' || e.key === 'e' || e.key === 'E' || e.key === '-') {
                          e.preventDefault();
                        }
                      }}
                      data-testid="item-quantity"
                    />
                    <p className="text-xs text-gray-500 mt-1">Whole numbers only</p>
                  </div>
                  <div>
                    <Label>Unit of Measure</Label>
                    <Input
                      value={currentItem.unit_of_measure || 'EA'}
                      disabled
                      className="bg-gray-50 text-gray-700"
                    />
                    <p className="text-xs text-gray-500 mt-1">Derived from material master</p>
                  </div>
                  
                  {/* Plant & Shipping Point Section */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Logistics
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Plant</Label>
                        <Select 
                          value={currentItem.plant || '__NONE__'} 
                          onValueChange={(v) => handleItemChange(activeItemIndex, 'plant', v === '__NONE__' ? '' : v)}
                        >
                          <SelectTrigger data-testid="item-plant-select">
                            <SelectValue placeholder="Select Plant" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__">-- None --</SelectItem>
                            {plants.map(p => (
                              <SelectItem key={p.plant_id} value={p.plant_id}>
                                {p.plant_id} - {p.plant_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {currentItem.plant_determined_from && (
                          <p className="text-xs text-green-600 mt-1">
                            Determined from: {currentItem.plant_determined_from === 'SHIP_TO_PARTY' ? 'Ship-to Party' : 
                              currentItem.plant_determined_from === 'MATERIAL_MASTER' ? 'Material Master' : 'Manual'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label>Shipping Point</Label>
                        <Select 
                          value={currentItem.shipping_point || '__NONE__'} 
                          onValueChange={(v) => handleItemChange(activeItemIndex, 'shipping_point', v === '__NONE__' ? '' : v)}
                          disabled={!currentItem.plant}
                        >
                          <SelectTrigger data-testid="item-shipping-point-select">
                            <SelectValue placeholder={!currentItem.plant ? "Select Plant first" : "Select Shipping Point"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__NONE__">-- None --</SelectItem>
                            {currentItemShippingPoints.length > 0 ? (
                              currentItemShippingPoints.map(sp => (
                                <SelectItem key={sp.shipping_point_id} value={sp.shipping_point_id}>
                                  {sp.shipping_point_id} - {sp.name}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="__NO_SP__" disabled>
                                No shipping points for this plant
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">Must be assigned to Plant</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Pricing Section */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      Indicative Pricing
                      {currentItem.pricing_loading && (
                        <span className="text-xs text-orange-600 animate-pulse">Loading...</span>
                      )}
                    </h4>
                    
                    {/* Indicative Pricing from Condition Records */}
                    {currentItem.indicative_pricing ? (
                      <div className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
                        <div className="grid grid-cols-4 gap-4">
                          {/* Base Price */}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Base Price</p>
                            <p className="text-lg font-semibold text-gray-800">
                              {currentItem.indicative_pricing.base_price !== null 
                                ? `${currentItem.indicative_pricing.currency} ${currentItem.indicative_pricing.base_price.toFixed(2)}`
                                : 'Not Found'}
                            </p>
                            {currentItem.indicative_pricing.base_price_per > 1 && (
                              <p className="text-xs text-gray-500">
                                per {currentItem.indicative_pricing.base_price_per} {currentItem.indicative_pricing.base_price_uom}
                              </p>
                            )}
                          </div>
                          
                          {/* Discounts */}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Total Discount</p>
                            {currentItem.indicative_pricing.discounts?.length > 0 ? (
                              <>
                                <p className="text-lg font-semibold text-green-600">
                                  -{currentItem.indicative_pricing.total_discount_percent}%
                                </p>
                                <p className="text-xs text-gray-500">
                                  -{currentItem.indicative_pricing.currency} {currentItem.indicative_pricing.total_discount_amount.toFixed(2)}
                                </p>
                              </>
                            ) : (
                              <p className="text-lg font-semibold text-gray-400">None</p>
                            )}
                          </div>
                          
                          {/* Net Price */}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Net Price</p>
                            <p className="text-xl font-bold text-orange-700">
                              {currentItem.indicative_pricing.net_price !== null
                                ? `${currentItem.indicative_pricing.currency} ${currentItem.indicative_pricing.net_price.toFixed(2)}`
                                : 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              for {currentItem.requested_quantity} {currentItem.unit_of_measure}
                            </p>
                          </div>
                          
                          {/* Unit Price */}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Unit Price</p>
                            <p className="text-lg font-semibold text-gray-700">
                              {currentItem.indicative_pricing.net_price !== null && currentItem.requested_quantity > 0
                                ? `${currentItem.indicative_pricing.currency} ${(currentItem.indicative_pricing.net_price / currentItem.requested_quantity).toFixed(2)}`
                                : 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">per {currentItem.unit_of_measure}</p>
                          </div>
                        </div>
                        
                        {/* Discount Details */}
                        {currentItem.indicative_pricing.discounts?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-orange-200">
                            <p className="text-xs text-gray-500 mb-2">Applied Discounts:</p>
                            <div className="flex flex-wrap gap-2">
                              {currentItem.indicative_pricing.discounts.map((d, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  {d.type}: {d.description} ({d.percent}%)
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {!currentItem.indicative_pricing.pricing_found && (
                          <p className="text-xs text-amber-600 mt-2">
                            No condition records found. Please maintain pricing data.
                          </p>
                        )}
                      </div>
                    ) : currentItem.material_id && header.customer_id ? (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200 text-center">
                        <p className="text-sm text-gray-500">
                          {currentItem.pricing_loading ? 'Loading pricing...' : 'No pricing data available'}
                        </p>
                        <button
                          type="button"
                          onClick={() => fetchIndicativePricing(activeItemIndex, currentItem.material_id, currentItem.requested_quantity)}
                          className="mt-2 text-sm text-orange-600 hover:text-orange-800 underline"
                        >
                          Refresh Pricing
                        </button>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200 text-center">
                        <p className="text-sm text-gray-400">
                          Select a material {!header.customer_id && 'and customer'} to see indicative pricing
                        </p>
                      </div>
                    )}
                    
                    {/* Manual Target Price Override */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Target Price (Override)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={currentItem.target_price}
                          onChange={(e) => handleItemChange(activeItemIndex, 'target_price', e.target.value)}
                          placeholder="Enter custom price if needed"
                        />
                        <p className="text-xs text-gray-500 mt-1">Override the calculated price if negotiating</p>
                      </div>
                      <div>
                        <Label>Pricing Notes</Label>
                        <Input
                          value={currentItem.pricing_info}
                          onChange={(e) => handleItemChange(activeItemIndex, 'pricing_info', e.target.value)}
                          placeholder="Optional pricing notes"
                          maxLength={500}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Availability Check */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="availability-check"
                        checked={currentItem.availability_check}
                        onCheckedChange={(checked) => handleItemChange(activeItemIndex, 'availability_check', checked)}
                      />
                      <Label htmlFor="availability-check" className="cursor-pointer">
                        Perform Availability Check (Optional)
                      </Label>
                    </div>
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Item Note</Label>
                    <textarea
                      value={currentItem.item_note}
                      onChange={(e) => handleItemChange(activeItemIndex, 'item_note', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
                      rows={2}
                      placeholder="Additional notes for this item..."
                      maxLength={500}
                    />
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default InquiryCreate;
